import { useState, useEffect } from 'react'
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    Alert,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
} from '@mui/material'
import {
    Add,
    Edit,
    Delete,
    Person,
    Business,
    Refresh,
    ExpandMore,
    Assignment,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
} from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useDepartments } from '@hooks/useDepartments'
import { useAuthStore } from '@store/authStore'
import Loading from '@components/Common/Loading'
import ConfirmDialog from '@components/Common/ConfirmDialog'
import { departmentFormAccessApi } from '@api/departmentFormAccess'
import { toast } from 'react-toastify'

const Departments = () => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const {
        departments,
        isLoading,
        refetch,
        createDepartment,
        updateDepartment,
        deleteDepartment,
        addUser,
        updateUser,
        deleteUser,
        setLeader,
        isCreating,
    } = useDepartments()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [userDialogOpen, setUserDialogOpen] = useState(false)
    const [editingDepartment, setEditingDepartment] = useState(null)
    const [selectedDepartment, setSelectedDepartment] = useState(null)
    const [editingUser, setEditingUser] = useState(null)

    const [deptForm, setDeptForm] = useState({
        name_en: '',
        name_ar: '',
        login_email: '',
        login_password: '',
    })

    const [userForm, setUserForm] = useState({
        name_en: '',
        name_ar: '',
        password: '',
        is_leader: false,
    })

    const [userFormError, setUserFormError] = useState('')

    // Confirm delete dialog state
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState(null) // { type: 'department'|'user', id, name }

    // Form Access state
    const [formAccessDialogOpen, setFormAccessDialogOpen] = useState(false)
    const [formAccessDept, setFormAccessDept] = useState(null)
    const [availableForms, setAvailableForms] = useState([])
    const [selectedForms, setSelectedForms] = useState([])
    const [visibleFieldsMap, setVisibleFieldsMap] = useState({})
    const [loadingFormAccess, setLoadingFormAccess] = useState(false)

    const isAdmin = user?.user_type === 'admin'

    // Fetch available forms when Form Access dialog opens
    useEffect(() => {
        if (formAccessDialogOpen && formAccessDept) {
            loadFormAccessData()
        }
    }, [formAccessDialogOpen, formAccessDept])

    const loadFormAccessData = async () => {
        setLoadingFormAccess(true)
        try {
            // Get all available forms
            const formsRes = await departmentFormAccessApi.getAvailableForms()
            setAvailableForms(formsRes.data || [])

            // Get this department's current form access
            const accessRes = await departmentFormAccessApi.getByDepartment(formAccessDept.id)
            const accessList = accessRes.data || []

            // Set selected forms and visible fields from current access
            setSelectedForms(accessList.map(a => a.form_id))
            const fieldsMap = {}
            accessList.forEach(a => {
                fieldsMap[a.form_id] = a.visible_fields || []
            })
            setVisibleFieldsMap(fieldsMap)
        } catch (error) {
            console.error('Failed to load form access:', error)
            toast.error('Failed to load form access')
        } finally {
            setLoadingFormAccess(false)
        }
    }

    const handleOpenFormAccessDialog = (dept) => {
        setFormAccessDept(dept)
        setFormAccessDialogOpen(true)
    }

    const handleCloseFormAccessDialog = () => {
        setFormAccessDialogOpen(false)
        setFormAccessDept(null)
        setSelectedForms([])
        setVisibleFieldsMap({})
    }

    const handleToggleForm = (formId) => {
        if (selectedForms.includes(formId)) {
            setSelectedForms(selectedForms.filter(id => id !== formId))
            const newMap = { ...visibleFieldsMap }
            delete newMap[formId]
            setVisibleFieldsMap(newMap)
        } else {
            setSelectedForms([...selectedForms, formId])
        }
    }

    const handleToggleField = (formId, fieldId) => {
        const currentFields = visibleFieldsMap[formId] || []
        if (currentFields.includes(fieldId)) {
            setVisibleFieldsMap({
                ...visibleFieldsMap,
                [formId]: currentFields.filter(id => id !== fieldId)
            })
        } else {
            setVisibleFieldsMap({
                ...visibleFieldsMap,
                [formId]: [...currentFields, fieldId]
            })
        }
    }

    const handleSaveFormAccess = async () => {
        try {
            await departmentFormAccessApi.assignForms(formAccessDept.id, selectedForms, visibleFieldsMap)
            toast.success('Form access saved successfully')
            handleCloseFormAccessDialog()
        } catch (error) {
            toast.error('Failed to save form access')
        }
    }

    const handleOpenDialog = (dept = null) => {
        if (dept) {
            setEditingDepartment(dept)
            setDeptForm({
                name_en: dept.name_en,
                name_ar: dept.name_ar || '',
                login_email: dept.login_email || '',
                login_password: '', // Don't pre-fill password
            })
        } else {
            setEditingDepartment(null)
            setDeptForm({ name_en: '', name_ar: '', login_email: '', login_password: '' })
        }
        setDialogOpen(true)
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
        setEditingDepartment(null)
        setDeptForm({ name_en: '', name_ar: '', login_email: '', login_password: '' })
    }

    const handleSaveDepartment = () => {
        if (!deptForm.name_en.trim()) return

        if (editingDepartment) {
            updateDepartment({ id: editingDepartment.id, data: deptForm })
        } else {
            createDepartment(deptForm)
        }
        handleCloseDialog()
    }

    const handleDeleteDepartment = (id, name) => {
        setDeleteTarget({ type: 'department', id, name: name || 'this department' })
        setConfirmDeleteOpen(true)
    }

    const handleOpenUserDialog = (dept, user = null) => {
        setSelectedDepartment(dept)
        if (user) {
            setEditingUser(user)
            setUserForm({
                name_en: user.name_en || '',
                name_ar: user.name_ar || '',
                password: '', // Don't populate password
                is_leader: user.is_department_leader || false,
            })
        } else {
            setEditingUser(null)
            setUserForm({ name_en: '', name_ar: '', password: '', is_leader: false })
        }
        setUserDialogOpen(true)
    }

    const handleCloseUserDialog = () => {
        setUserDialogOpen(false)
        setSelectedDepartment(null)
        setEditingUser(null)
        setUserForm({ name_en: '', name_ar: '', password: '', is_leader: false })
        setUserFormError('')
    }

    const handleSaveUser = async () => {
        setUserFormError('')

        if (!userForm.name_en.trim()) {
            setUserFormError(language === 'ar' ? 'الاسم مطلوب' : 'Name (English) is required')
            return
        }
        if (!editingUser && !userForm.password.trim()) {
            setUserFormError(language === 'ar' ? 'كلمة المرور مطلوبة' : 'Password is required')
            return
        }
        if (userForm.password && userForm.password.length < 6) {
            setUserFormError(language === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters')
            return
        }

        try {
            if (editingUser) {
                // Update existing user
                const userData = { name_en: userForm.name_en, name_ar: userForm.name_ar }
                if (userForm.password) {
                    userData.password = userForm.password
                }
                await updateUser({ userId: editingUser.id, userData })
                // Also update leader status if changed
                if (userForm.is_leader !== editingUser.is_department_leader) {
                    await setLeader({ userId: editingUser.id, isLeader: userForm.is_leader })
                }
            } else {
                // Add new user with is_leader flag
                await addUser({ departmentId: selectedDepartment.id, userData: { ...userForm, is_leader: userForm.is_leader } })
            }
            handleCloseUserDialog()
            refetch()
        } catch (error) {
            const errorMessage = error?.response?.data?.message || 'Failed to save user'
            setUserFormError(errorMessage)
        }
    }

    const handleDeleteUserConfirm = (userId, userName) => {
        setDeleteTarget({ type: 'user', id: userId, name: userName || 'this user' })
        setConfirmDeleteOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return
        try {
            if (deleteTarget.type === 'department') {
                deleteDepartment(deleteTarget.id)
            } else {
                await deleteUser(deleteTarget.id)
                refetch()
            }
        } catch (error) {
            console.error('Delete failed', error)
        }
        setConfirmDeleteOpen(false)
        setDeleteTarget(null)
    }

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false)
        setDeleteTarget(null)
    }

    const getDeptName = (dept) => {
        if (language === 'ar' && dept.name_ar) {
            return dept.name_ar
        }
        return dept.name_en
    }

    if (isLoading) {
        return <Loading message={t('common.loading')} />
    }

    return (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography variant="h4" fontWeight={700}>
                    {t('department.title')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => refetch()}
                    >
                        {t('common.refresh')}
                    </Button>
                    {isAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenDialog()}
                        >
                            {t('department.addDepartment')}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Departments Grid */}
            {departments.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Business sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">
                        {t('department.noDepartments') || 'No departments found'}
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {departments.map((dept) => (
                        <Grid item xs={12} md={6} lg={4} key={dept.id}>
                            <Card sx={{ height: '100%', borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: 'primary.light',
                                                    color: 'primary.contrastText',
                                                }}
                                            >
                                                <Business />
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" fontWeight={600}>
                                                    {getDeptName(dept)}
                                                </Typography>
                                                {dept.name_ar && language !== 'ar' && (
                                                    <Typography variant="body2" color="text.secondary" dir="rtl">
                                                        {dept.name_ar}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        {isAdmin && (
                                            <Box>
                                                <IconButton size="small" onClick={() => handleOpenDialog(dept)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteDepartment(dept.id, getDeptName(dept))}
                                                    title={language === 'ar' ? 'حذف القسم' : 'Delete Department'}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>

                                    <Chip
                                        label={`${dept.user_count || 0} ${t('department.users')}`}
                                        size="small"
                                        sx={{ mt: 2 }}
                                    />

                                    {dept.users && dept.users.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Divider sx={{ mb: 1 }} />
                                            <List dense disablePadding>
                                                {dept.users.map((deptUser) => (
                                                    <ListItem
                                                        key={deptUser.id}
                                                        sx={{ px: 0 }}
                                                        secondaryAction={isAdmin && (
                                                            <Box>
                                                                <IconButton
                                                                    size="small"
                                                                    color={deptUser.is_department_leader ? "warning" : "default"}
                                                                    onClick={() => setLeader({ userId: deptUser.id, isLeader: !deptUser.is_department_leader })}
                                                                    title={deptUser.is_department_leader
                                                                        ? (language === 'ar' ? 'إزالة من القيادة' : 'Remove as leader')
                                                                        : (language === 'ar' ? 'تعيين كقائد' : 'Set as leader')}
                                                                >
                                                                    {deptUser.is_department_leader ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleOpenUserDialog(dept, deptUser)}
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleDeleteUserConfirm(deptUser.id, deptUser.name_en || deptUser.name_ar)}
                                                                    title={language === 'ar' ? 'حذف المستخدم' : 'Delete User'}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    >
                                                        <ListItemAvatar sx={{ minWidth: 40 }}>
                                                            <Avatar sx={{
                                                                width: 32,
                                                                height: 32,
                                                                fontSize: '0.875rem',
                                                                bgcolor: deptUser.is_department_leader ? 'warning.main' : undefined
                                                            }}>
                                                                {deptUser.name_en?.charAt(0)}
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {language === 'ar' ? deptUser.name_ar : deptUser.name_en}
                                                                    {deptUser.is_department_leader && (
                                                                        <Chip
                                                                            label={language === 'ar' ? 'قائد' : 'Leader'}
                                                                            size="small"
                                                                            color="warning"
                                                                            sx={{ height: 18, fontSize: '0.65rem' }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            }
                                                            secondary={dept.login_email || (language === 'ar' ? 'يسجل الدخول بكلمة مروره' : 'Logs in with password')}
                                                            primaryTypographyProps={{ variant: 'body2' }}
                                                            secondaryTypographyProps={{ variant: 'caption' }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                </CardContent>

                                {isAdmin && (
                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                        <Button
                                            size="small"
                                            startIcon={<Person />}
                                            onClick={() => handleOpenUserDialog(dept)}
                                        >
                                            {t('department.addUser')}
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<Assignment />}
                                            onClick={() => handleOpenFormAccessDialog(dept)}
                                        >
                                            {t('department.manageForms') || 'Manage Forms'}
                                        </Button>
                                    </CardActions>
                                )}
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Department Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingDepartment ? t('department.editDepartment') : t('department.addDepartment')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label={t('department.nameEn')}
                        value={deptForm.name_en}
                        onChange={(e) => setDeptForm((prev) => ({ ...prev, name_en: e.target.value }))}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label={t('department.nameAr')}
                        value={deptForm.name_ar}
                        onChange={(e) => setDeptForm((prev) => ({ ...prev, name_ar: e.target.value }))}
                        margin="normal"
                        dir="rtl"
                    />
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        {language === 'ar' ? 'بيانات دخول الجهة' : 'Department Login Credentials'}
                    </Typography>
                    <TextField
                        fullWidth
                        label={language === 'ar' ? 'البريد الإلكتروني للدخول' : 'Login Email'}
                        value={deptForm.login_email}
                        onChange={(e) => setDeptForm((prev) => ({ ...prev, login_email: e.target.value }))}
                        margin="normal"
                        type="email"
                        placeholder="department@example.com"
                        helperText={language === 'ar' ? 'جميع المستخدمين يستخدمون هذا البريد للدخول' : 'All users login with this email'}
                    />

                    {/* Department Users Section - Only show when editing */}
                    {editingDepartment && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {language === 'ar' ? 'مستخدمو القسم' : 'Department Users'}
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => {
                                        handleCloseDialog()
                                        handleOpenUserDialog(editingDepartment)
                                    }}
                                >
                                    {language === 'ar' ? 'إضافة مستخدم' : 'Add User'}
                                </Button>
                            </Box>

                            {editingDepartment.users && editingDepartment.users.length > 0 ? (
                                <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                                    {editingDepartment.users.map((deptUser, idx) => (
                                        <ListItem
                                            key={deptUser.id}
                                            divider={idx < editingDepartment.users.length - 1}
                                            secondaryAction={
                                                <Box>
                                                    <IconButton
                                                        size="small"
                                                        color={deptUser.is_department_leader ? "warning" : "default"}
                                                        onClick={() => setLeader({ userId: deptUser.id, isLeader: !deptUser.is_department_leader })}
                                                        title={deptUser.is_department_leader
                                                            ? (language === 'ar' ? 'إزالة من القيادة' : 'Remove as leader')
                                                            : (language === 'ar' ? 'تعيين كقائد' : 'Set as leader')}
                                                    >
                                                        {deptUser.is_department_leader ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            handleCloseDialog()
                                                            handleOpenUserDialog(editingDepartment, deptUser)
                                                        }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteUserConfirm(deptUser.id, deptUser.name_en || deptUser.name_ar)}
                                                        title={language === 'ar' ? 'حذف المستخدم' : 'Delete User'}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            }
                                        >
                                            <ListItemAvatar sx={{ minWidth: 40 }}>
                                                <Avatar sx={{
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: '0.875rem',
                                                    bgcolor: deptUser.is_department_leader ? 'warning.main' : 'primary.main'
                                                }}>
                                                    {deptUser.name_en?.charAt(0)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {language === 'ar' ? (deptUser.name_ar || deptUser.name_en) : deptUser.name_en}
                                                        {deptUser.is_department_leader && (
                                                            <Chip
                                                                label={language === 'ar' ? 'قائد' : 'Leader'}
                                                                size="small"
                                                                color="warning"
                                                                sx={{ height: 18, fontSize: '0.65rem' }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={editingDepartment.login_email || (language === 'ar' ? 'يستخدم البريد المشترك' : 'Uses shared email')}
                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                secondaryTypographyProps={{ variant: 'caption' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    {language === 'ar' ? 'لا يوجد مستخدمون في هذا القسم' : 'No users in this department'}
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleSaveDepartment} disabled={isCreating}>
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit User Dialog */}
            <Dialog open={userDialogOpen} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingUser
                        ? (language === 'ar' ? 'تعديل المستخدم' : 'Edit User')
                        : t('department.addUser')} - {selectedDepartment && getDeptName(selectedDepartment)}
                </DialogTitle>
                <DialogContent>
                    {userFormError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {userFormError}
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        label={t('department.nameEn')}
                        value={userForm.name_en}
                        onChange={(e) => setUserForm((prev) => ({ ...prev, name_en: e.target.value }))}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label={t('department.nameAr')}
                        value={userForm.name_ar}
                        onChange={(e) => setUserForm((prev) => ({ ...prev, name_ar: e.target.value }))}
                        margin="normal"
                        dir="rtl"
                    />
                    <TextField
                        fullWidth
                        label={t('auth.password')}
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm((prev) => ({ ...prev, password: e.target.value }))}
                        margin="normal"
                        required={!editingUser}
                        helperText={editingUser ? (language === 'ar' ? 'اتركه فارغاً للحفاظ على كلمة المرور الحالية' : 'Leave empty to keep current password') : ''}
                    />
                    <Divider sx={{ my: 2 }} />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={userForm.is_leader}
                                onChange={(e) => setUserForm((prev) => ({ ...prev, is_leader: e.target.checked }))}
                                color="warning"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight={500}>
                                    {language === 'ar' ? 'قائد القسم' : 'Department Leader'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {language === 'ar'
                                        ? 'يمكنه إدارة المستخدمين والوصول لكافة البيانات'
                                        : 'Can manage users and access all department data'}
                                </Typography>
                            </Box>
                        }
                        sx={{ mt: 1, display: 'flex' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUserDialog}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleSaveUser}>
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Form Access Dialog */}
            <Dialog open={formAccessDialogOpen} onClose={handleCloseFormAccessDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t('department.manageFormAccess') || 'Manage Form Access'} - {formAccessDept?.name_en}
                </DialogTitle>
                <DialogContent>
                    {loadingFormAccess ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>{t('common.loading') || 'Loading...'}</Box>
                    ) : (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('department.formAccessDescription') || 'Select which forms this department can access. For each form, you can optionally configure which fields are visible.'}
                            </Typography>

                            {availableForms.length === 0 ? (
                                <Alert severity="info">{t('department.noFormsAvailable') || 'No forms available'}</Alert>
                            ) : (
                                availableForms.map((form) => {
                                    const isSelected = selectedForms.includes(form.id)
                                    // Parse fields if it's a JSON string
                                    let formFields = form.fields || []
                                    if (typeof formFields === 'string') {
                                        try {
                                            formFields = JSON.parse(formFields)
                                        } catch { formFields = [] }
                                    }
                                    if (!Array.isArray(formFields)) formFields = []
                                    const selectedVisibleFields = visibleFieldsMap[form.id] || []

                                    return (
                                        <Accordion key={form.id} expanded={isSelected}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMore />}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onChange={() => handleToggleForm(form.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography fontWeight={500}>{form.title}</Typography>
                                                            {form.title_ar && (
                                                                <Typography variant="caption" color="text.secondary" dir="rtl">
                                                                    {form.title_ar}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    Visible Fields (leave empty for all):
                                                </Typography>
                                                <FormGroup row>
                                                    {formFields.map((field) => (
                                                        <FormControlLabel
                                                            key={field.id}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={selectedVisibleFields.includes(field.id)}
                                                                    onChange={() => handleToggleField(form.id, field.id)}
                                                                />
                                                            }
                                                            label={field.label_en || field.id}
                                                        />
                                                    ))}
                                                </FormGroup>
                                                {selectedVisibleFields.length > 0 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                        {selectedVisibleFields.length} field(s) selected
                                                    </Typography>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    )
                                })
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseFormAccessDialog}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleSaveFormAccess}>
                        {t('department.saveFormAccess') || 'Save Form Access'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                open={confirmDeleteOpen}
                title={language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
                message={
                    deleteTarget?.type === 'department'
                        ? (language === 'ar'
                            ? `هل أنت متأكد من حذف القسم "${deleteTarget?.name}"؟ سيتم حذف جميع المستخدمين المرتبطين.`
                            : `Are you sure you want to delete department "${deleteTarget?.name}"? All associated users will be removed.`)
                        : (language === 'ar'
                            ? `هل أنت متأكد من حذف المستخدم "${deleteTarget?.name}"؟`
                            : `Are you sure you want to delete user "${deleteTarget?.name}"?`)
                }
                confirmText={language === 'ar' ? 'حذف' : 'Delete'}
                cancelText={language === 'ar' ? 'إلغاء' : 'Cancel'}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </Box>
    )
}

export default Departments
